import React, { useState } from 'react'
import { Button as AntButton, DatePicker, Input, Popconfirm, Space, Table, TableColumnsType, Tooltip } from 'antd'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import dayjs from 'dayjs'
import { ITrct, ITrctFilters } from '../../interfaces'
import { Grid, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import { downloadTrct } from '../../services/api'
import './style.scss'


interface ITableTrct {
  data: ITrct[];
  sortFunction: any;
  loading: boolean;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  currentPage: number;
  handlePagination: (page: number, pageSize?: number) => void;
  handleDelete: (file: string) => void;
  filters: ITrctFilters;
  cpfFilter: string;
  onFiltersChange: (filters: ITrctFilters) => void;
  onCpfFilter: (cpf: string) => void;
}

const { RangePicker } = DatePicker

// Dropdown de filtro de texto/número; aplica ao confirmar e limpa com o botão
const TextFilterDropdown: React.FC<{
  value: string;
  placeholder: string;
  inputMode?: 'text' | 'numeric';
  onApply: (value: string) => void;
  close: () => void;
}> = ({ value, placeholder, inputMode = 'text', onApply, close }) => {
  const [draft, setDraft] = useState(value)
  const apply = (next: string) => {
    onApply(next)
    close()
  }

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        inputMode={inputMode}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(inputMode === 'numeric' ? e.target.value.replace(/\D/g, '') : e.target.value)}
        onPressEnter={() => apply(draft.trim())}
        style={{ display: 'block', marginBottom: 8, width: 220 }}
      />
      <Space>
        <AntButton type="primary" size="small" onClick={() => apply(draft.trim())}>
          Filtrar
        </AntButton>
        <AntButton
          size="small"
          onClick={() => {
            setDraft('')
            apply('')
          }}
        >
          Limpar
        </AntButton>
      </Space>
    </div>
  )
}


const TableTrct: React.FC<ITableTrct> = ({
                                           data,
                                           sortFunction,
                                           loading,
                                           pageSize,
                                           totalPages,
                                           totalCount,
                                           currentPage,
                                           handlePagination,
                                           handleDelete,
                                           filters,
                                           cpfFilter,
                                           onFiltersChange,
                                           onCpfFilter
                                         }) => {


  const onDownload = (fileName: string) => {
    downloadTrct(fileName, 'TrctWorker/')
  }

  // Agrupando os arquivos por CPF, ignorando duplicados
  const groupedData = data.reduce((acc, current) => {
    const cpf = current.cpf
    if (!acc[cpf]) {
      acc[cpf] = []
    }
    acc[cpf].push(current)
    return acc
  }, {} as { [key: string]: ITrct[] })

  const filterIcon = (active: boolean) => <FilterAltIcon fontSize="small" color={active ? 'secondary' : 'inherit'} />

  const columns: TableColumnsType<any> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => record.files[0].name,
      filterIcon: () => filterIcon(!!filters.name),
      filterDropdown: ({ close }) => (
        <TextFilterDropdown
          value={filters.name}
          placeholder="Filtrar por nome"
          onApply={(name) => onFiltersChange({ ...filters, name })}
          close={close}
        />
      )
    },
    {
      title: 'CPF',
      key: 'cpf',
      render: (_, record) => record.cpf,
      filterIcon: () => filterIcon(!!cpfFilter),
      filterDropdown: ({ close }) => (
        <TextFilterDropdown
          value={cpfFilter}
          placeholder="Filtrar por CPF"
          inputMode="numeric"
          onApply={onCpfFilter}
          close={close}
        />
      )
    },
    {
      title: 'Data de Demissão',
      key: 'dataDemissao',
      render: (_, record) => record.files[0].dataDemissao || '-',
      filterIcon: () => filterIcon(!!filters.demissaFrom || !!filters.demissaTo),
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <RangePicker
            format="DD/MM/YYYY"
            placeholder={['De', 'Até']}
            value={[
              filters.demissaFrom ? dayjs(filters.demissaFrom, 'YYYYMMDD') : null,
              filters.demissaTo ? dayjs(filters.demissaTo, 'YYYYMMDD') : null
            ]}
            onChange={(dates) => {
              onFiltersChange({
                ...filters,
                demissaFrom: dates?.[0] ? dates[0].format('YYYYMMDD') : '',
                demissaTo: dates?.[1] ? dates[1].format('YYYYMMDD') : ''
              })
              if (!dates) close()
            }}
          />
          <div style={{ marginTop: 8 }}>
            <AntButton size="small" onClick={close}>
              Fechar
            </AntButton>
          </div>
        </div>
      )
    },
    {
      title: 'Quantidade de Arquivos',
      key: 'fileCount',
      render: (_, record) => record.files.length,
      filterIcon: () => filterIcon(!!filters.fileCount),
      filterDropdown: ({ close }) => (
        <TextFilterDropdown
          value={filters.fileCount}
          placeholder="Quantidade exata"
          inputMode="numeric"
          onApply={(fileCount) => onFiltersChange({ ...filters, fileCount })}
          close={close}
        />
      )
    }
  ]
  const handleChange = (pagination: any, filters: any, sorter: any) => {
    sortFunction(sorter)
    handlePagination(pagination.current, pagination.pageSize)
  }

  return (
    <Table
      locale={{
        triggerDesc: 'Ordenação descendente',
        triggerAsc: 'Ordenação ascendente',
        cancelSort: 'Cancelar ordenação',
        emptyText: 'Nenhum registro encontrado'
      }}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: totalCount,
        showSizeChanger: true
        // onChange: handlePagination
      }}
      columns={columns}
      dataSource={Object.keys(groupedData).map(cpf => ({
        cpf,
        files: groupedData[cpf]
      }))}
      rowKey={(record) => record.cpf}
      onChange={handleChange}
      loading={loading}
      expandable={{
        expandedRowRender: (record) => (
          <div className="file-container">
            {record.files.map((file: ITrct, index: number) => (
              <Grid item xs={12} sm={12} md={12} lg={12} key={index}>
                <div className="file-item" style={{ display: 'flex', alignItems: 'center', margin: '2px' }}>
                  <p style={{ marginLeft: 15 }}>
                    {file.fileName} - {file.createdAt}
                  </p>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                    <Tooltip title="Download">
                      <IconButton onClick={() => onDownload(file.fileName)}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <Popconfirm title="Deseja excluir o arquivo??" cancelText={'Cancelar'} okText={'Excluir'}
                                  onConfirm={() => handleDelete(file.fileName)}>
                        <IconButton>
                          <DeleteIcon />
                        </IconButton>
                      </Popconfirm>

                    </Tooltip>
                  </div>
                </div>
              </Grid>
            ))}
          </div>
        ),
        rowExpandable: (record) => record.files.length > 0,
        expandedRowClassName: () => 'file-container'
      }}

    />
  )
}

export default TableTrct
